/** A mention being typed: where its prefix sits, how far the reader has typed, and what. */
export interface IMentionMatch {
  /** Index of the prefix character(s). */
  start: number;
  /** Index of the caret — the query ends here. */
  caret: number;
  /** Index where the mention token ends: the first whitespace or split at or after the caret. */
  end: number;
  /** What was typed between the prefix and the caret. */
  query: string;
}

/** The text after a pick, and where the caret goes. */
export interface IMentionInsertion {
  text: string;
  caret: number;
}

/** The option shape the matcher needs — `KuikaMentions`' option satisfies it. */
export interface IMentionSearchable {
  value: string;
  searchLabel?: string;
}

const WHITESPACE: RegExp = /\s/;
const LINE_BREAK: RegExp = /[\r\n]/;

const isWhitespace = (character: string): boolean => WHITESPACE.test(character);

/**
 * The mention the caret is in, if any: the LAST `prefix` before the caret, standing at the start of
 * the text or right after whitespace (so `a@b`, an e-mail address, never opens a list), with no
 * `split` between it and the caret. A mention also never runs across a line break — a query cannot
 * hold one, and a pick would otherwise replace text on the line above.
 */
export const findMention = (text: string, caret: number, prefix: string, split: string): IMentionMatch | null => {
  if (prefix.length === 0 || caret < prefix.length || caret > text.length) {
    return null;
  }
  const beforeCaret: string = text.slice(0, caret);
  const start: number = beforeCaret.lastIndexOf(prefix);
  if (start === -1) {
    return null;
  }
  if (start > 0 && !isWhitespace(text.charAt(start - 1))) {
    return null;
  }
  const query: string = beforeCaret.slice(start + prefix.length);
  if ((split.length > 0 && query.includes(split)) || LINE_BREAK.test(query)) {
    return null;
  }
  let end: number = caret;
  while (end < text.length && !isWhitespace(text.charAt(end)) && !(split.length > 0 && text.startsWith(split, end))) {
    end++;
  }
  return { start, caret, end, query };
};

/**
 * Replaces the whole mention token — prefix, what was typed and any rest of the word after the
 * caret — with `prefix + value + split`, and puts the caret after it. A `split` already standing
 * right after the token is absorbed, so a pick in the middle of a sentence does not leave two.
 */
export const insertMention = (
  text: string,
  match: IMentionMatch,
  value: string,
  prefix: string,
  split: string
): IMentionInsertion => {
  const before: string = text.slice(0, match.start);
  let after: string = text.slice(match.end);
  if (split.length > 0 && after.startsWith(split)) {
    after = after.slice(split.length);
  }
  const inserted: string = `${prefix}${value}${split}`;
  return { text: `${before}${inserted}${after}`, caret: before.length + inserted.length };
};

/**
 * Case-insensitive "contains" on `searchLabel ?? value`. `toLowerCase`, never a locale-aware case
 * function: under a Turkish locale `"I".toLocaleLowerCase()` is a dotless `ı`, and a parameter
 * named `ID` would stop matching `@id`.
 */
export const defaultMentionFilter = (query: string, option: IMentionSearchable): boolean =>
  (option.searchLabel ?? option.value).toLowerCase().includes(query.toLowerCase());
